import { useSEO } from '../../hooks/useSEO';
import { SUPPORT_EMAIL } from '../../lib/partnerAuth';
import styles from './FAQ.module.css';

const faqs = [
  {
    q: 'How do I share my referral link?',
    a: `<p>Go to your <strong>Dashboard</strong> and copy your unique referral link. You can share it on social media, your website, YouTube description, Instagram bio, or directly with your audience.</p>
<p>You can also create <strong>custom campaign links</strong> from the Campaigns tab to track different channels separately (e.g., one link for Instagram, another for YouTube).</p>`,
  },
  {
    q: 'How are earnings calculated?',
    a: `<p>You earn <strong>$0.10 for every verified app install</strong> that comes through your referral link. Earnings are tracked in real-time on your dashboard.</p>
<p>This means every time someone clicks your link and installs the LuvLust app, you earn $0.10 — regardless of whether they make a purchase.</p>`,
  },
  {
    q: 'What counts as a verified install?',
    a: `<p>A verified install is when a new user downloads and opens the LuvLust app after clicking your referral link. We use secure device tracking to verify each install.</p>
<p>The following do <strong>not</strong> count:</p>
<ul>
<li>Re-installing the app on the same device (device deduplication is active)</li>
<li>Installing on an emulator or virtual device</li>
<li>Installs outside the attribution window</li>
</ul>`,
  },
  {
    q: 'How does fraud detection work?',
    a: `<p>We use <strong>device-level deduplication</strong> to ensure fair earnings. Each physical device can only generate one install earning within a 90-day period.</p>
<p>This means uninstalling and re-installing the app on the same phone will not generate additional earnings. Our system tracks unique device identifiers to prevent abuse.</p>
<p>Partners found engaging in fraudulent activity may have their accounts suspended.</p>`,
  },
  {
    q: 'When and how are payouts processed?',
    a: `<p>The minimum payout is <strong>$10 unpaid balance</strong>. Earnings below $10 roll over month-to-month until you reach the minimum.</p>
<p>Payouts are processed <strong>monthly</strong> once you're eligible. You can view your payout history and status on the <strong>Earnings</strong> tab.</p>
<p>Status meanings:</p>
<ul>
<li><strong>Accumulating</strong> — earned but below the $10 minimum</li>
<li><strong>Pending payout</strong> — eligible and awaiting monthly processing</li>
<li><strong>Paid</strong> — payout sent for that month</li>
</ul>
<p>If you have questions about a specific payout, email us at <strong>${SUPPORT_EMAIL}</strong>.</p>`,
  },
  {
    q: 'How do I track my performance?',
    a: `<p>Your <strong>Dashboard</strong> shows real-time stats including:</p>
<ul>
<li><strong>Link Clicks</strong> — how many people clicked your referral link</li>
<li><strong>Installs</strong> — verified app installs from your link</li>
<li><strong>Purchases</strong> — users who made in-app purchases (for reference)</li>
<li><strong>Total Earned</strong> — your cumulative earnings</li>
<li><strong>Top Countries</strong> — where your installs are coming from</li>
</ul>
<p>You can filter by platform (iOS/Android) and date range to analyze specific periods.</p>`,
  },
  {
    q: 'How do campaigns work?',
    a: `<p>Campaigns let you create <strong>custom short links</strong> to track different marketing channels. For example:</p>
<ul>
<li><strong>luvlustapp.com/r/myig</strong> — for your Instagram bio</li>
<li><strong>luvlustapp.com/r/myyt</strong> — for your YouTube videos</li>
<li><strong>luvlustapp.com/r/myblog</strong> — for your blog</li>
</ul>
<p>Each campaign tracks its own clicks, installs, and earnings separately, so you can see which channels perform best.</p>
<p>Go to the <strong>Campaigns</strong> tab to create a new campaign link.</p>`,
  },
  {
    q: 'How do I contact support?',
    a: `<p>For any questions about payouts, your account, or partnership inquiries, email us at:</p>
<p><strong>${SUPPORT_EMAIL}</strong></p>
<p>You can also click the contact strip at the bottom of any partner page to send us an email.</p>`,
  },
];

export function PartnerFAQ() {
  useSEO({
    title: 'How to Use — LuvLust Partners',
    description:
      'Frequently asked questions about the LuvLust partner program — earnings, installs, payouts, and campaigns.',
  });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>How to Use</h1>
      <p className={styles.subtitle}>
        Everything you need to know about the LuvLust partner program.
      </p>

      <div className={styles.faqList}>
        {faqs.map((faq, i) => (
          <details key={i} className={styles.faqItem}>
            <summary className={styles.faqQuestion}>{faq.q}</summary>
            <div
              className={styles.faqAnswer}
              dangerouslySetInnerHTML={{ __html: faq.a }}
            />
          </details>
        ))}
      </div>
    </div>
  );
}
